import { CircularProgress, IconButton, InputBase } from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import SearchIcon from "@material-ui/icons/Search";
import { memo, useMemo, useState } from "react";
import { formatQuantity, mapType, useDebaunce } from "utils";

import {
  CardInfo,
  CardItem,
  CardList,
  CardTop,
  ChipRow,
  CountLabel,
  EmptyState,
  FooterCell,
  FooterCellLabel,
  FooterGrid,
  FooterLabel,
  FooterRow,
  FooterValue,
  IdChip,
  LoadMoreButton,
  PageTitle,
  ProductName,
  SearchCard,
  SearchField,
  SmallLabel,
  StockBlock,
  StockValue,
  TitleRow,
  TypeChip,
  Wrapper
} from "./style";

const PAGE_SIZE = 30;
const LOAD_MORE_STEP = 50;

const stockColor = value => {
  if (value < 0) return "#DA3737";
  if (value === 0) return "#9e9e9e";
  return "#416D0B";
};

// memo: enquanto o usuário digita, o card só re-renderiza se a própria linha
// mudar — sem isso cada tecla re-renderizava a lista inteira e travava.
const StockCardBase = ({ row, hasScheduledDebits }) => {
  const scheduledDebits = row.anticipated_scheduled_debits || 0;
  const accountedBalance = row.summary - scheduledDebits;
  const unit = row.purchase_type === 1 ? "Kg" : "un";
  return (
    <CardItem>
      <CardTop>
        <CardInfo>
          <ProductName>{row.product_base_name}</ProductName>
          <ChipRow>
            <IdChip>ID {row.product_base_id}</IdChip>
            <TypeChip>{mapType(row.purchase_type)}</TypeChip>
          </ChipRow>
        </CardInfo>
        <StockBlock>
          <SmallLabel>Estoque disponível</SmallLabel>
          <StockValue style={{ color: stockColor(row.summary) }}>
            {formatQuantity(row.summary)}
          </StockValue>
          <SmallLabel>{unit}</SmallLabel>
        </StockBlock>
      </CardTop>
      {hasScheduledDebits ? (
        <FooterGrid>
          <FooterCell>
            <FooterCellLabel>Saldo atual</FooterCellLabel>
            <FooterValue>{formatQuantity(accountedBalance)}</FooterValue>
          </FooterCell>
          <FooterCell>
            <FooterCellLabel>Saídas agendadas · dom. 23:59</FooterCellLabel>
            <FooterValue
              style={{ color: scheduledDebits < 0 ? "#DA3737" : "#3f3f3f" }}
            >
              {formatQuantity(scheduledDebits)}
            </FooterValue>
          </FooterCell>
        </FooterGrid>
      ) : (
        <FooterRow>
          <FooterLabel>Saldo atual</FooterLabel>
          <FooterValue>{formatQuantity(row.summary)}</FooterValue>
        </FooterRow>
      )}
    </CardItem>
  );
};

const StockCard = memo(StockCardBase);

export const MobileStockCards = ({
  rows,
  hasScheduledDebits,
  loading,
  title
}) => {
  const [query, setQuery] = useState("");
  const [filterQuery, setFilterQuery] = useState("");
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);

  const applyFilter = value => {
    setFilterQuery(value);
    setVisibleCount(PAGE_SIZE);
  };
  // 800ms segue o padrão de busca do app (Dashboard, Estoque por SKU); com
  // menos que isso o filtro dispara no meio da digitação e trava o teclado.
  const debouncedFilter = useDebaunce({ fn: applyFilter, delay: 800 });

  const handleQueryChange = value => {
    setQuery(value);
    debouncedFilter(value);
  };

  const clearQuery = () => {
    setQuery("");
    // debouncedFilter("") substitui o timeout pendente da digitação (que
    // aplicaria o valor antigo); applyFilter("") limpa a lista na hora.
    debouncedFilter("");
    applyFilter("");
  };

  const filteredRows = useMemo(() => {
    const search = filterQuery.trim().toLowerCase();
    if (!search) return rows;
    return rows.filter(
      row =>
        row.product_base_name?.toLowerCase().includes(search) ||
        String(row.product_base_id).includes(search)
    );
  }, [rows, filterQuery]);

  const visibleRows = useMemo(
    () => filteredRows.slice(0, visibleCount),
    [filteredRows, visibleCount]
  );
  const remainingCount = filteredRows.length - visibleRows.length;
  const isSearching = query !== filterQuery;

  return (
    <Wrapper>
      <TitleRow>
        <PageTitle>{title}</PageTitle>
        <CountLabel>
          {isSearching
            ? "Pesquisando…"
            : `${filteredRows.length} de ${rows.length}`}
        </CountLabel>
      </TitleRow>

      <SearchCard>
        <SearchField>
          <SearchIcon style={{ color: "#757575", fontSize: 20 }} />
          <InputBase
            value={query}
            onChange={e => handleQueryChange(e.target.value)}
            placeholder="Pesquisar produto ou ID"
            style={{ flex: 1, fontSize: "16px", color: "#3f3f3f" }}
            inputProps={{ "aria-label": "Pesquisar produto ou ID" }}
          />
          {isSearching && (
            <CircularProgress size={18} style={{ color: "#416D0B" }} />
          )}
          {query.length > 0 && (
            <IconButton
              size="small"
              onClick={clearQuery}
              aria-label="Limpar pesquisa"
              style={{ background: "#efefef", color: "#616161" }}
            >
              <CloseIcon style={{ fontSize: 14 }} />
            </IconButton>
          )}
        </SearchField>
      </SearchCard>

      <CardList
        style={{
          opacity: isSearching ? 0.5 : 1,
          transition: "opacity .15s"
        }}
      >
        {visibleRows.map(row => (
          <StockCard
            key={row.product_base_id}
            row={row}
            hasScheduledDebits={hasScheduledDebits}
          />
        ))}

        {remainingCount > 0 && (
          <LoadMoreButton
            onClick={() => setVisibleCount(count => count + LOAD_MORE_STEP)}
          >
            Carregar mais ({remainingCount} restantes)
          </LoadMoreButton>
        )}

        {!loading && filteredRows.length === 0 && (
          <EmptyState>Nenhum produto encontrado.</EmptyState>
        )}
        {loading && filteredRows.length === 0 && (
          <EmptyState>Carregando…</EmptyState>
        )}
      </CardList>
    </Wrapper>
  );
};
